/* eslint-disable react/prop-types */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef
} from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Button, Col, Row } from 'react-bootstrap';
import DynamicNormalForm from './dynamicNormalForm';
import { DynamicTableForm } from './dynamicTableForm';
import {
  calculateFieldVisibility,
  generateValidationSchema
} from './validationSchema';
import { yupResolver } from '@hookform/resolvers/yup';
import { parse, format, isValid } from 'date-fns';
import { ModalForm } from './dynamicModalForm';
import { useDependencyManager } from './useDependencyManager';
import apiClient from 'api/client';

const DynamicFormV2 = ({ data, onSubmit, modalType, header, mode, view }) => {
  const { fields: dataFields, form } = data;

  // State management
  const [fields, setFields] = useState(dataFields);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasDefaultValue, setHasDefaultValue] = useState(mode === 'edit');
  const [modalVisibility, setModalVisibility] = useState({});
  const [modalDataMap, setModalDataMap] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [modalFieldOptionsMap, setModalFieldOptionsMap] = useState({});
  const [formInitialized, setFormInitialized] = useState(false);
  const [tableData, setTableData] = useState({});

  // Refs to prevent unnecessary re-render and track initialization
  const previousDataRef = useRef();
  const initialModalDataSet = useRef(false);
  const lastVisibilityTriggers = useRef({});
  const fieldChangeTimeouts = useRef(new Map());
  const initializationComplete = useRef(false);

  // FIXED: Memoized default values - simplified
  const processedDefaultValues = useMemo(() => {
    if (!dataFields) return {};

    const defaults = {};

    Object.values(dataFields).forEach(field => {
      // Handle regular field defaults
      if (field.defaultValue !== undefined && field.defaultValue !== null) {
        defaults[field.id] = field.defaultValue;
      }

      // Handle modal data as form values
      if (
        field.type === 'jsonb' &&
        field.modal?.rows &&
        field.modal.rows.length > 0
      ) {
        defaults[field.id] = field.modal.rows;
      }

      // Set appropriate defaults for different field types
      switch (field.type) {
        case 'toggle':
          if (defaults[field.id] === undefined) {
            defaults[field.id] = false;
          }
          break;
        case 'dropdown':
          if (field.multiSelect && defaults[field.id] === undefined) {
            defaults[field.id] = [];
          }
          break;
        case 'checkbox':
          if (defaults[field.id] === undefined) {
            defaults[field.id] = [];
          }
          break;
        case 'jsonb':
          if (defaults[field.id] === undefined) {
            defaults[field.id] = [];
          }
          break;
        default:
          // Only set empty string if no default value exists
          if (defaults[field.id] === undefined) {
            defaults[field.id] = field.multiSelect ? [] : '';
          }
      }
    });

    return defaults;
  }, [dataFields]);

  // FIXED: Enhanced field visibility logic - memoized to prevent re-calculations
  const getFieldVisibility = useCallback((fields, formValues) => {
    const hiddenFields = calculateFieldVisibility(fields, formValues);
    const visibilityMap = new Map();

    // Convert Set to Map for backwards compatibility
    hiddenFields.forEach(fieldId => {
      visibilityMap.set(fieldId, false); // false = hidden
    });

    return visibilityMap;
  }, []);
  // Form setup with memoized resolver
  const formResolver = useMemo(() => {
    return async (data, context, options) => {
      const schema = generateValidationSchema({
        fields,
        formValues: data // Pass current form values instead of visibility map
      });
      return yupResolver(schema)(data, context, options);
    };
  }, [fields]);

  const methods = useForm({
    resolver: formResolver,
    mode: 'onChange',
    defaultValues: processedDefaultValues
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    watch,
    setValue,
    reset,
    getValues
  } = methods;

  // Watch form values for dependency management
  const formValues = watch();

  // FIXED: Update form metadata callback - prevents infinite loops
  const updateFormMetadata = useCallback((fieldId, options) => {
    setFields(prevFields => {
      const currentField = prevFields[fieldId];
      if (
        currentField &&
        (!currentField.options ||
          JSON.stringify(currentField.options) !== JSON.stringify(options))
      ) {
        // console.log(
        //   `Updating options for ${fieldId}:`,
        //   options.length,
        //   'items'
        // );
        return {
          ...prevFields,
          [fieldId]: { ...currentField, options }
        };
      }
      return prevFields;
    });
  }, []);

  // FIXED: Initialize dependency manager with proper parameters
  const {
    buildDependencyChain,
    fetchDependentData,
    clearTracking,
    initializeFields
  } = useDependencyManager(fields, formValues, setValue, updateFormMetadata);

  // FIXED: Initialize form with default values
  useEffect(() => {
    if (
      processedDefaultValues &&
      Object.keys(processedDefaultValues).length > 0 &&
      !formInitialized
    ) {
      console.log('Initializing form with defaults:', processedDefaultValues);
      reset(processedDefaultValues);
      setFormInitialized(true);
    }
  }, [processedDefaultValues, formInitialized, reset]);

  // FIXED: Set up modal data map for edit mode
  useEffect(() => {
    if (mode === 'edit' && !initialModalDataSet.current) {
      const modalMap = {};
      Object.values(fields)
        .filter(
          field =>
            field.type === 'jsonb' &&
            field.modal?.rows &&
            field.modal.rows.length > 0
        )
        .forEach(field => {
          modalMap[field.id] = field.modal.rows;
        });

      if (Object.keys(modalMap).length > 0) {
        setModalDataMap(modalMap);
        initialModalDataSet.current = true;
      }
    }
  }, [mode, fields]);

  // FIXED: Initialize selectedRows from defaultValue
  useEffect(() => {
    if (selectedRows.length === 0) {
      const formReportField = Object.values(fields).find(
        f => f.type === 'form_report'
      );
      if (
        formReportField?.defaultValue !== undefined &&
        formReportField.defaultValue !== null
      ) {
        const defaultValue = Array.isArray(formReportField.defaultValue)
          ? formReportField.defaultValue
          : [formReportField.defaultValue];
        setSelectedRows(defaultValue);
        setValue(formReportField.id, defaultValue);
      }
    }
  }, [fields, selectedRows.length]);

  // FIXED: Reset flags when mode changes
  useEffect(() => {
    if (mode !== 'edit') {
      initialModalDataSet.current = false;
      setFormInitialized(false);
      initializationComplete.current = false;
    }
  }, [mode]);

  // Watch only specific fields that affect visibility
  const visibilityTriggerFields = useMemo(() => {
    return Object.values(fields)
      .filter(
        field =>
          (field.type === 'radio_group' && field.hide) ||
          (field.type === 'toggle' && field.hide)
      )
      .map(field => field.id);
  }, [fields]);

  const visibilityTriggerValues = watch(visibilityTriggerFields);

  // Calculate visibility map only when trigger fields change
  const [visibilityMap, setVisibilityMap] = useState(new Map());

  useEffect(() => {
    const currentTriggers = {};
    visibilityTriggerFields.forEach((fieldId, index) => {
      currentTriggers[fieldId] = visibilityTriggerValues[index];
    });

    const triggersChanged =
      JSON.stringify(currentTriggers) !==
      JSON.stringify(lastVisibilityTriggers.current);

    if (triggersChanged) {
      lastVisibilityTriggers.current = currentTriggers;
      const newVisibilityMap = getFieldVisibility(fields, formValues);
      setVisibilityMap(newVisibilityMap);

      // CRITICAL: Force immediate re-validation when visibility changes
      // Use nextTick to ensure form values are updated first
      setTimeout(() => {
        methods.trigger();
      }, 10);
    }
  }, [
    visibilityTriggerFields,
    visibilityTriggerValues,
    fields,
    formValues,
    getFieldVisibility,
    methods
  ]);

  // FIXED: Optimized field change handler with proper debouncing
  const handleFieldChange = useCallback(
    (fieldId, value) => {
      // Clear any existing timeout for this field
      if (fieldChangeTimeouts.current.has(fieldId)) {
        clearTimeout(fieldChangeTimeouts.current.get(fieldId));
      }

      // Set the form value immediately for UI responsiveness
      setValue(fieldId, value);

      // Skip processing if no value or no dependents
      if (
        !value ||
        value === '' ||
        value === 'null' ||
        (Array.isArray(value) && value.length === 0)
      ) {
        const field = fields[fieldId];
        if (field?.dependents && field.dependents.length > 0) {
          // Clear dependents for empty values
          console.log(`Clearing dependents for empty ${fieldId}`);
          fetchDependentData(fieldId, value, apiClient);
        }
        return;
      }

      const field = fields[fieldId];
      if (!field?.dependents || field.dependents.length === 0) {
        return;
      }

      // Debounce the API calls
      const timeoutId = setTimeout(async () => {
        try {
          console.log(`Processing dependencies for ${fieldId}:`, value);
          await fetchDependentData(fieldId, value, apiClient);
        } catch (error) {
          console.error(`Error processing dependents for ${fieldId}:`, error);
        } finally {
          fieldChangeTimeouts.current.delete(fieldId);
        }
      }, 300);

      fieldChangeTimeouts.current.set(fieldId, timeoutId);
    },
    [setValue, fetchDependentData, fields, apiClient]
  );

  // FIXED: Radio Group Change Handler
  const handleRadioGroupChange = useCallback(
    (field, value) => {
      setValue(field.id, value);

      // Reset hidden fields based on radio selection
      if (field.hide) {
        Object.entries(field.hide).forEach(([radioVal, hideFieldIds]) => {
          if (radioVal !== value) {
            hideFieldIds.forEach(fieldId => {
              const targetField = fields[fieldId];
              if (targetField) {
                const defaultValue =
                  targetField.defaultValue ??
                  (targetField.type === 'dropdown' && targetField.multiSelect
                    ? []
                    : '');
                setValue(fieldId, defaultValue);
              }
            });
          }
        });
      }

      // CRITICAL: Force immediate re-validation after radio change
      setTimeout(() => {
        methods.trigger(); // Re-validate entire form
      }, 0);

      // Handle dependencies
      if (field.dependents && field.dependents.length > 0) {
        handleFieldChange(field.id, value);
      }
    },
    [setValue, fields, handleFieldChange, methods]
  );

  // FIXED: Toggle Change Handler
  const handleToggleChange = useCallback(
    (toggleField, newValue) => {
      setValue(toggleField.id, newValue);

      if (toggleField?.hide && Array.isArray(toggleField.hide[0])) {
        const fieldsToReset = newValue
          ? toggleField.hide[0]
          : toggleField.hide[1] || [];
        fieldsToReset.forEach(fieldId => {
          const field = Object.values(fields).find(f => f.id === fieldId);
          if (field) {
            setValue(fieldId, field.defaultValue ?? '');
          }
        });
      }

      // Handle dependencies
      if (toggleField.dependents && toggleField.dependents.length > 0) {
        handleFieldChange(toggleField.id, newValue);
      }
    },
    [setValue, fields, handleFieldChange]
  );

  // FIXED: Expose setValue globally for special cases
  useEffect(() => {
    window.setFormValue = setValue;
    return () => {
      delete window.setFormValue;
    };
  }, [setValue]);

  // FIXED: Process initial dependencies for edit mode
  useEffect(() => {
    if (
      formInitialized &&
      mode === 'edit' &&
      !initializationComplete.current &&
      Object.keys(fields).length > 0
    ) {
      initializationComplete.current = true;

      const timeoutId = setTimeout(async () => {
        console.log('Starting edit mode initialization...');

        try {
          // Find fields with both default values and dependents
          const fieldsWithDependencies = Object.values(fields).filter(
            field =>
              field.dependents &&
              field.dependents.length > 0 &&
              field.defaultValue !== undefined &&
              field.defaultValue !== null &&
              field.defaultValue !== '' &&
              !(
                Array.isArray(field.defaultValue) &&
                field.defaultValue.length === 0
              )
          );

          // console.log(
          //   'Fields with dependencies for edit mode:',
          //   fieldsWithDependencies.map(f => ({
          //     id: f.id,
          //     defaultValue: f.defaultValue
          //   }))
          // );

          // Process each field sequentially
          for (const field of fieldsWithDependencies) {
            console.log(`Processing edit mode dependencies for ${field.id}`);
            try {
              await fetchDependentData(
                field.id,
                field.defaultValue,
                apiClient,
                true
              );
              await new Promise(resolve => setTimeout(resolve, 800));
            } catch (error) {
              console.error(
                `Error processing edit mode dependencies for ${field.id}:`,
                error
              );
            }
          }

          console.log('Edit mode initialization completed');
        } catch (error) {
          console.error('Error in edit mode initialization:', error);
        }
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [formInitialized, mode, fields, fetchDependentData, apiClient]);

  // Date handling functions
  const handleDateRangeChange = useCallback(
    (field, start, end) => {
      if (!start || !end) return;
      if (new Date(start) > new Date(end)) {
        console.error('Start date cannot be after end date.');
        return;
      }
      const start_date = format(new Date(start), 'yyyy-MM-dd');
      const end_date = format(new Date(end), 'yyyy-MM-dd');
      setValue(field.id, { start_date, end_date });
    },
    [setValue]
  );

  const handleRangeChange = useCallback(
    (field, start, end) => {
      if (!start || !end) return;
      if (new Date(start) > new Date(end)) {
        console.error('Start date cannot be after end date.');
        return;
      }
      const start_date = `yyyy-${format(new Date(start), 'MM-dd')}`;
      const end_date = `yyyy-${format(new Date(end), 'MM-dd')}`;
      setValue(field.id, { start_date, end_date });
    },
    [setValue]
  );

  const handleSelectRow = useCallback(
    (field, rowId) => {
      if (field?.isMulti === false) {
        setSelectedRows([rowId]);
        setValue(field.id, rowId);
      } else {
        setSelectedRows(prev => {
          const newSelected = prev.includes(rowId)
            ? prev.filter(id => id !== rowId)
            : [...prev, rowId];
          setValue(field.id, newSelected);
          return newSelected;
        });
      }
    },
    [setValue]
  );

  const handleRowChange = useCallback((tableName, updatedRows) => {
    setTableData(prevState => ({
      ...prevState,
      [tableName]: updatedRows
    }));
  }, []);

  // Optimized date formatting function
  const formatDatesInObject = useCallback(obj => {
    const formattedObj = {};
    for (const key of Object.keys(obj)) {
      const value = obj[key];
      if (value instanceof Date && isValid(value)) {
        formattedObj[key] = format(value, 'yyyy-MM-dd');
      } else if (typeof value === 'string') {
        const parsedDate = parse(
          value,
          "EEE MMM dd yyyy HH:mm:ss 'GMT'XXX",
          new Date()
        );
        if (isValid(parsedDate)) {
          formattedObj[key] = format(parsedDate, 'yyyy-MM-dd');
        } else {
          formattedObj[key] = value;
        }
      } else {
        formattedObj[key] = value;
      }
    }
    return formattedObj;
  }, []);

  // Submit handler
  const onSubmitHandler = useCallback(
    async formData => {
      if (isProcessing) return;
      setIsProcessing(true);

      try {
        // Log current hidden fields for debugging
        const hiddenFields = calculateFieldVisibility(fields, formData);
        console.log('Hidden fields during submit:', Array.from(hiddenFields));

        const allFormValues = getValues();
        const formattedData = formatDatesInObject(allFormValues);

        const finalSubmitData = {
          ...formattedData,
          ...modalDataMap
        };

        // FAILSAFE: Ensure that form_report fields with default values are not submitted empty
        Object.values(fields).forEach(field => {
          if (
            field.type === 'form_report' &&
            field.defaultValue !== undefined &&
            field.defaultValue !== null
          ) {
            if (
              !finalSubmitData[field.id] ||
              (Array.isArray(finalSubmitData[field.id]) &&
                finalSubmitData[field.id].length === 0)
            ) {
              const defaultValue = Array.isArray(field.defaultValue)
                ? field.defaultValue
                : [field.defaultValue];
              finalSubmitData[field.id] = defaultValue;
              console.log(
                `Failsafe: Injected default value for ${field.id}:`,
                defaultValue
              );
            }
          }
        });

        await onSubmit(finalSubmitData);
      } catch (error) {
        console.error('Submission error:', error);
      } finally {
        setIsProcessing(false);
      }
    },
    [
      isProcessing,
      getValues,
      formatDatesInObject,
      modalDataMap,
      onSubmit,
      fields
    ]
  );

  // Modal event listeners
  useEffect(() => {
    const handleModalOptionsUpdate = event => {
      const { modalId, fieldId, options } = event.detail;
      setModalFieldOptionsMap(prev => ({
        ...prev,
        [`${modalId}.${fieldId}`]: options
      }));
    };

    const handleModalFieldReset = event => {
      const { modalId, fieldId, value } = event.detail;
      window.dispatchEvent(
        new CustomEvent(`resetModalField_${modalId}`, {
          detail: { fieldId, value }
        })
      );
    };

    window.addEventListener(
      'updateModalFieldOptions',
      handleModalOptionsUpdate
    );
    window.addEventListener('resetModalField', handleModalFieldReset);

    return () => {
      window.removeEventListener(
        'updateModalFieldOptions',
        handleModalOptionsUpdate
      );
      window.removeEventListener('resetModalField', handleModalFieldReset);
    };
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      fieldChangeTimeouts.current.forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
      fieldChangeTimeouts.current.clear();
      clearTracking();
    };
  }, [clearTracking]);

  // Modal handlers
  const handleModalSubmit = useCallback(
    ({ data, modalId }) => {
      setModalDataMap(prev => ({
        ...prev,
        [modalId]: data
      }));
      setValue(modalId, data);
    },
    [setValue]
  );

  const toggleModal = useCallback((modalId, isVisible) => {
    setModalVisibility(prev => ({
      ...prev,
      [modalId]: isVisible
    }));
  }, []);

  // Get visible fields based on visibility map
  const visibleFields = useMemo(() => {
    const fieldList = Object.values(fields);
    const hiddenFields = calculateFieldVisibility(fields, formValues);

    return fieldList.filter(field => {
      // If field is in hidden set, don't show it
      if (hiddenFields.has(field.id)) {
        return false;
      }

      return true;
    });
  }, [fields, formValues]);

  // Render field function
  const renderField = useCallback(
    field => {
      const currentField = fields[field.id];

      if (currentField.type === 'jsonb') {
        return (
          <Col key={currentField.id} lg={12}>
            <div className="text-end mt-3">
              <Button
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleModal(currentField.id, true);
                }}
                className="mb-3"
              >
                {currentField.button_name || 'Add New'}
              </Button>
            </div>
            <ModalForm
              modalConfig={{
                ...currentField.modal,
                id: currentField.id
              }}
              onClose={visible => toggleModal(currentField.id, visible)}
              show={!!modalVisibility[currentField.id]}
              onSubmit={handleModalSubmit}
              initialData={currentField.modal?.rows || []}
              modalFieldOptions={modalFieldOptionsMap}
              parentFormValues={formValues}
            />
          </Col>
        );
      }

      return (
        <Col
          lg={
            currentField.lg ||
            (modalType
              ? currentField.type === 'textarea' ||
                currentField.type === 'radio_group'
                ? 12
                : 6
              : currentField.type === 'textarea' ||
                currentField.type === 'radio_group' ||
                currentField.type === 'sectionHeader' ||
                currentField.type === 'Selection' ||
                currentField.type === 'form_report'
              ? 12
              : 4)
          }
          key={currentField.id}
          className="g-lg-4 g-2"
        >
          <DynamicNormalForm
            fields={fields}
            metadata={currentField}
            control={control}
            mode={mode}
            register={methods.register}
            errors={errors}
            onToggleChange={
              currentField.type === 'toggle'
                ? value => handleToggleChange(currentField, value)
                : undefined
            }
            onRadioGroupChange={
              currentField.type === 'radio_group'
                ? value => handleRadioGroupChange(currentField, value)
                : undefined
            }
            hasDefaultValue={hasDefaultValue}
            setHasDefaultValue={setHasDefaultValue}
            handleDateRangeChange={(start, end) =>
              handleDateRangeChange(currentField, start, end)
            }
            handleMonthDayChange={(start, end) =>
              handleRangeChange(currentField, start, end)
            }
            modalDataMap={modalDataMap}
            hideFields={() => {}}
            onFormMetadataChange={updateFormMetadata}
            handleSelectRow={handleSelectRow}
            selectedRows={selectedRows}
            onFieldChange={handleFieldChange}
          />
        </Col>
      );
    },
    [
      fields,
      modalType,
      control,
      mode,
      methods.register,
      errors,
      handleToggleChange,
      handleRadioGroupChange,
      hasDefaultValue,
      handleDateRangeChange,
      handleRangeChange,
      modalDataMap,
      updateFormMetadata,
      handleSelectRow,
      selectedRows,
      handleFieldChange,
      toggleModal,
      modalVisibility,
      handleModalSubmit,
      modalFieldOptionsMap,
      formValues
    ]
  );

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmitHandler, errors => {
          console.log('Validation errors:', errors);
        })}
        noValidate
      >
        <Row className="gx-2 gy-3">
          {visibleFields.map(field => renderField(field))}
        </Row>

        {Object.values(fields)
          .filter(field => field.table)
          .map(field => (
            <DynamicTableForm
              key={field.id}
              control={methods.control}
              errors={errors}
              tableConfig={field}
              onRowChange={handleRowChange}
            />
          ))}

        <div className="text-end mt-3">
          {!view && (
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={isSubmitting || isProcessing}
              className="w-100 w-sm-auto"
            >
              {isProcessing || isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          )}
        </div>
      </form>
    </FormProvider>
  );
};

export default React.memo(DynamicFormV2);
